import { createSlice } from '@reduxjs/toolkit';
import {
  AIContextMode,
  AIResponseMetadata,
  AIResponseStatusType,
  ChatbotVariant,
  ConversationItemType,
  HtmlSnapshotResponseState,
} from '../types/aiChatbot';
import { ChatActionOptions, ChatHtmlSnapshot, OnboardingChatContext } from '../types/signlaRTypes';
import {
  applyHtmlSnapshotToResponse,
  createHtmlSnapshotResponse,
} from './htmlSnapshots';
import { fetchAIPrompts } from './actions';
import { logChatFailure } from './diagnostics';
import { hasVisibleAIContent, MAX_CHAT_PROGRESS_HISTORY_ENTRIES, normalizeChatProgress } from './progress';

const recordProgressDuration = (response: HtmlSnapshotResponseState) => {
  if (!response.progressSuppressed && response.progressStartedAt !== undefined) {
    response.progressElapsedMs = Math.max(0, response.progressElapsedMs ?? 0, Date.now() - response.progressStartedAt);
  }
};

const freezeProgress = (response: HtmlSnapshotResponseState) => {
  recordProgressDuration(response);
  response.progressSuppressed = true;
};

const getObjectValue = (payload: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const exactValue = payload[key];
    if (exactValue !== undefined && exactValue !== null && String(exactValue).trim()) {
      return exactValue;
    }

    const matchingKey = Object.keys(payload).find(
      (payloadKey) => payloadKey.toLowerCase() === key.toLowerCase()
    );
    const matchingValue = matchingKey ? payload[matchingKey] : undefined;
    if (matchingValue !== undefined && matchingValue !== null && String(matchingValue).trim()) {
      return matchingValue;
    }
  }

  return undefined;
};

const normalizeAIResponseMetadata = (payload: unknown): AIResponseMetadata | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const response = payload as Record<string, unknown>;
  const nestedPayload = getObjectValue(response, ['data', 'payload', 'result', 'value']);
  const nestedMetadata = nestedPayload && typeof nestedPayload === 'object'
    ? normalizeAIResponseMetadata(nestedPayload)
    : undefined;
  const aiModelName = getObjectValue(response, [
    'aiModelName',
    'modelName',
    'model',
  ]) ?? nestedMetadata?.aiModelName;
  const aiReasoning = getObjectValue(response, [
    'aiReasoning',
    'modelReasoning',
    'reasoning',
    'reasoningMode',
    'reasoningEffort',
  ]) ?? nestedMetadata?.aiReasoning;

  if (!aiModelName && !aiReasoning) return undefined;

  return {
    aiModelName: aiModelName ? String(aiModelName) : undefined,
    aiReasoning: aiReasoning ? String(aiReasoning) : undefined,
  };
};

export interface OnboardingSectionInfo {
  stepDisplayName: string;
  progressPercent: number;
  status: 'not_started' | 'in_progress' | 'auto_completed' | 'completed';
}

export type OnboardingStepStatus = 'not_started' | 'in_progress' | 'auto_completed' | 'completed';

export interface OnboardingOverviewStep {
  stepId: string;
  stepDisplayName: string;
  order: number;
  status: OnboardingStepStatus;
  progressPercent: number;
  isActive: boolean;
}

export interface OnboardingOverview {
  context?: OnboardingChatContext;
  steps: OnboardingOverviewStep[];
  activeStepId: string | null;
  overallProgressPercent: number;
  subjectDisplayName: string | null;
  speakerIsSubject: boolean;
}

export const isProfileOnboardingFinished = (overview: OnboardingOverview | null | undefined): boolean =>
  !!overview?.speakerIsSubject &&
  overview.steps.length > 0 &&
  overview.steps.every(step => step.status === 'completed' || step.status === 'auto_completed');

export const onboardingActionsKey = (memberId: string, roomId: string, sessionId: string, agentId: string) =>
  JSON.stringify([memberId, roomId, sessionId, agentId]);

export interface AiChatbotState {
  pendingRoomQuickReply?: { roomId: string; id: string; question: string };
  liveCompletedOnboardingSteps: Record<string, string[]>;
  dismissedOnboardingSteps: Record<string, string[]>;
  dismissedOnboardingActions: string[];
  /** Conversations whose onboarding finished live, so returning members never see completion actions again. */
  liveCompletedOnboardingActions: string[];
  isExternalOpen: boolean;
  pendingRoomMayaOpenId: string | null;
  conversations: ConversationItemType[];
  conversationSessionId: string | undefined;
  aiResponseStatus: AIResponseStatusType;
  htmlSnapshotResponses: Record<string, HtmlSnapshotResponseState>;
  activeResponseByLegacyTarget: Record<string, string>;
  pendingResponseId?: string;
  suggestedQuestions: string[];
  loadingPrompts: boolean;
  isSummaryMode: boolean;
  contextMode: AIContextMode;
  activeChatbotVariant: ChatbotVariant;
  pendingResponseMetadata?: AIResponseMetadata;
  /** Label for the "talking about" subject bubble (null = hidden). */
  activeSubjectLabel: string | null;
  /** Current onboarding section progress (null = hidden). */
  onboardingSection: OnboardingSectionInfo | null;
  /** Full onboarding overview from server (null = hidden). */
  onboardingOverview: OnboardingOverview | null;
}

const initialState: AiChatbotState = {
  liveCompletedOnboardingSteps: {},
  dismissedOnboardingSteps: {},
  dismissedOnboardingActions: [],
  liveCompletedOnboardingActions: [],
  isExternalOpen: false,
  pendingRoomMayaOpenId: null,
  conversations: [],
  conversationSessionId: undefined,
  aiResponseStatus: AIResponseStatusType.READY,
  htmlSnapshotResponses: {},
  activeResponseByLegacyTarget: {},
  pendingResponseId: undefined,
  suggestedQuestions: [],
  loadingPrompts: false,
  isSummaryMode: false,
  contextMode: AIContextMode.WEB_SEARCH,
  activeChatbotVariant: 'maya',
  pendingResponseMetadata: undefined,
  activeSubjectLabel: null,
  onboardingSection: null,
  onboardingOverview: null,
};

const dismissCurrentOnboardingActions = (state: AiChatbotState) => {
  const overview = state.onboardingOverview;
  const context = overview?.context;
  if (!overview?.speakerIsSubject || !context ||
      !overview.steps.length ||
      context.sessionId !== state.conversationSessionId || context.agentId !== state.activeChatbotVariant) return;
  const key = onboardingActionsKey(context.memberId, context.roomId, context.sessionId, context.agentId);
  if (!state.dismissedOnboardingActions.includes(key)) state.dismissedOnboardingActions.push(key);
};

const aiChatbotSlice = createSlice({
  name: 'aiChatbot',
  initialState,
  reducers: {
    requestRoomQuickReply(state, action: { payload: { roomId: string; id: string; question: string } }) {
      state.pendingRoomQuickReply = action.payload;
    },
    clearRoomQuickReply(state) {
      state.pendingRoomQuickReply = undefined;
    },
    completeOnboardingActions(state, action: { payload: {
      stepId?: string; context?: OnboardingChatContext; speakerIsSubject: boolean; stepOnly?: boolean;
    } }) {
      const overview = state.onboardingOverview;
      const context = action.payload.context;
      if (action.payload.stepOnly && context && action.payload.speakerIsSubject && action.payload.stepId &&
          context.sessionId === state.conversationSessionId && context.agentId === state.activeChatbotVariant) {
        const key = onboardingActionsKey(context.memberId, context.roomId, context.sessionId, context.agentId);
        const completed = state.liveCompletedOnboardingSteps[key] ?? [];
        if (!completed.includes(action.payload.stepId)) completed.push(action.payload.stepId);
        state.liveCompletedOnboardingSteps[key] = completed;
      }
      if (!overview?.speakerIsSubject || !action.payload.speakerIsSubject || !context ||
          overview.context?.memberId !== context.memberId || overview.context?.roomId !== context.roomId ||
          overview.context?.sessionId !== context.sessionId || overview.context?.agentId !== context.agentId) return;
      const step = overview.steps.find(item => item.stepId === action.payload.stepId);
      if (step) {
        step.status = 'completed';
        step.progressPercent = 100;
        step.isActive = false;
      }
      const key = onboardingActionsKey(context.memberId, context.roomId, context.sessionId, context.agentId);
      if (step) {
        const completed = state.liveCompletedOnboardingSteps[key] ?? [];
        if (!completed.includes(step.stepId)) completed.push(step.stepId);
        state.liveCompletedOnboardingSteps[key] = completed;
      }
      if (!action.payload.stepOnly && isProfileOnboardingFinished(overview) && !state.liveCompletedOnboardingActions.includes(key)) {
        state.liveCompletedOnboardingActions.push(key);
      }
    },
    dismissOnboardingActions(state, action: { payload: string }) {
      if (!state.dismissedOnboardingActions.includes(action.payload)) {
        state.dismissedOnboardingActions.push(action.payload);
      }
    },
    dismissCompletedOnboardingSteps(state, action: { payload: string }) {
      state.dismissedOnboardingSteps[action.payload] = [...(state.liveCompletedOnboardingSteps[action.payload] ?? [])];
    },
    addConversationItem(state, action) {
      const payload = action.payload;
      if (payload?.isSent) {
        for (const response of Object.values(state.htmlSnapshotResponses)) delete response.actionOffer;
      }
      state.conversations.push({
        ...payload,
        createdAt: payload?.createdAt ?? new Date().toISOString(),
      });
    },
    beginAIResponse(
      state,
      action: {
        payload: {
          responseId: string;
          legacyTargets: string[];
          sessionId?: string;
        };
      }
    ) {
      const { responseId, legacyTargets, sessionId } = action.payload;
      if (!responseId || legacyTargets.length === 0) return;

      const primaryLegacyTarget = legacyTargets[0];
      state.htmlSnapshotResponses[responseId] = createHtmlSnapshotResponse(
        responseId,
        primaryLegacyTarget,
        sessionId
      );
      state.htmlSnapshotResponses[responseId].progressStartedAt = Date.now();

      if (!state.conversations.some((item) => item.responseId === responseId)) {
        state.conversations.push({
          isSent: false,
          text: '',
          responseId,
          createdAt: new Date().toISOString(),
        });
      }

      for (const legacyTarget of legacyTargets) {
        state.activeResponseByLegacyTarget[legacyTarget] = responseId;
      }

      state.pendingResponseId = responseId;
      state.pendingResponseMetadata = undefined;
      state.aiResponseStatus = AIResponseStatusType.PENDING;
    },
    applyChatProgress(state, action: { payload: unknown }) {
      const progress = normalizeChatProgress(action.payload);
      if (!progress) return;
      const response = state.htmlSnapshotResponses[progress.responseId];
      if (!response || response.isComplete || response.progressSuppressed
        || state.activeResponseByLegacyTarget[progress.legacyTarget] !== progress.responseId
        || response.agentId !== progress.agentId
        || state.activeChatbotVariant !== progress.agentId
        || (response.sessionId && response.sessionId !== progress.sessionId)
        || progress.sequence <= (response.progress?.sequence ?? 0)) return;
      if (progress.stage === 'source_accepted') {
        // Marks the latest marker for this source without replacing the visible stage.
        const history = response.progressHistory ?? [];
        for (let index = history.length - 1; index >= 0; index--) {
          if (history[index].sourceKey === progress.sourceKey) {
            history[index].sourceAccepted = true;
            break;
          }
        }
        return;
      }
      response.sessionId = progress.sessionId;
      response.progress = progress;
      response.progressSuppressed ??= false;
      recordProgressDuration(response);
      if (progress.stage === 'finished') {
        freezeProgress(response);
      } else {
        const history = response.progressHistory ??= [];
        const previous = history[history.length - 1];
        if (previous?.stage === progress.stage && previous.sourceHost === progress.sourceHost
          && previous.sourceTitle === progress.sourceTitle && previous.sourceKey === progress.sourceKey) {
          history[history.length - 1] = previous.sourceAccepted ? { ...progress, sourceAccepted: true } : progress;
        } else {
          history.push(progress);
          if (history.length > MAX_CHAT_PROGRESS_HISTORY_ENTRIES) history.shift();
        }
      }
    },
    applyChatActionOptions(state, action: { payload: ChatActionOptions }) {
      const offer = action.payload;
      const response = state.htmlSnapshotResponses[offer.responseId];
      const latest = state.conversations[state.conversations.length - 1];
      if (!response || response.streamingStopped || latest?.isSent !== false
        || latest.responseId !== offer.responseId
        || state.activeChatbotVariant !== offer.agentId
        || (response.agentId && response.agentId !== offer.agentId)
        || response.legacyTarget !== offer.legacyTarget
        || (response.sessionId && response.sessionId !== offer.sessionId)
        || (state.conversationSessionId && state.conversationSessionId !== offer.sessionId)) {
        logChatFailure('action-options-unknown-response');
        return;
      }
      response.actionOffer = { offerId: offer.offerId, options: offer.options };
    },
    suppressAIProgress(state) {
      for (const responseId of new Set(Object.values(state.activeResponseByLegacyTarget))) {
        const response = state.htmlSnapshotResponses[responseId];
        if (response) {
          response.progressSuppressed = true;
          response.progressHistory = undefined;
        }
      }
    },
    applyHtmlSnapshot(state, action: { payload: ChatHtmlSnapshot }) {
      const snapshot = action.payload;
      const current = state.htmlSnapshotResponses[snapshot.responseId];
      if (current?.streamingStopped) return;
      const hasOptimisticResponse = state.conversations.some(
        (item) => item.responseId === snapshot.responseId && !item.isSent
      );

      if (!current || !hasOptimisticResponse) {
        logChatFailure('snapshot-unknown-response');
        return;
      }

      if (current.isComplete) {
        logChatFailure('snapshot-after-completion');
        return;
      }

      const targetResponseId =
        state.activeResponseByLegacyTarget[snapshot.legacyTarget];
      if (targetResponseId && targetResponseId !== snapshot.responseId) {
        logChatFailure('snapshot-busy-target');
        return;
      }

      try {
        const next = applyHtmlSnapshotToResponse(current, snapshot);
        if (next === current) return;

        if (hasVisibleAIContent(snapshot.html)) {
          freezeProgress(next);
        }
        state.htmlSnapshotResponses[snapshot.responseId] = next;
        state.activeResponseByLegacyTarget[snapshot.legacyTarget] =
          snapshot.responseId;
        state.aiResponseStatus = AIResponseStatusType.PRINTING;
      } catch (error) {
        logChatFailure('snapshot-reducer', error);
      }
    },
    appendLegacyResponseContent(
      state,
      action: {
        payload: {
          responseId: string;
          legacyTarget: string;
          html: string;
        };
      }
    ) {
      const { responseId, legacyTarget, html } = action.payload;
      if (!html) return;
      if (state.activeResponseByLegacyTarget[legacyTarget] !== responseId) {
        return;
      }

      const response = state.htmlSnapshotResponses[responseId];
      const conversation = state.conversations.find(
        (item) => item.responseId === responseId && !item.isSent
      );
      if (!response || !conversation || response.streamingStopped) return;

      response.legacyExtras.push(html);
      // Retain the original append-only rendering path for older backends. Once
      // snapshots exist, the UI renders segments plus legacyExtras instead.
      conversation.text = (conversation.text || '') + html;
      if (hasVisibleAIContent(conversation.text)) {
        freezeProgress(response);
      }
      conversation.createdAt = conversation.createdAt ?? new Date().toISOString();
      state.aiResponseStatus = AIResponseStatusType.PRINTING;
    },
    completeAIResponse(
      state,
      action: { payload: { responseId: string; legacyTarget: string } }
    ) {
      const { responseId, legacyTarget } = action.payload;
      if (state.activeResponseByLegacyTarget[legacyTarget] !== responseId) {
        return;
      }

      const response = state.htmlSnapshotResponses[responseId];
      if (response) {
        freezeProgress(response);
        response.isComplete = true;
      }

      for (const [target, activeResponseId] of Object.entries(
        state.activeResponseByLegacyTarget
      )) {
        if (activeResponseId === responseId) {
          delete state.activeResponseByLegacyTarget[target];
        }
      }
      state.aiResponseStatus = AIResponseStatusType.READY;
    },
    setAIResponseCancellationSupport(state, action: { payload: { responseId: string; sessionId: string } }) {
      const response = state.htmlSnapshotResponses[action.payload.responseId];
      if (!response || response.isComplete || !action.payload.sessionId
        || response.agentId !== state.activeChatbotVariant
        || state.activeResponseByLegacyTarget[response.legacyTarget] !== response.responseId
        || (response.sessionId && response.sessionId !== action.payload.sessionId)) return;
      response.sessionId = action.payload.sessionId;
      response.supportsRequestCancellation = true;
    },
    setAIResponseBackendCancellationStatus(state, action: { payload: {
      responseId: string; status: 'pending' | 'accepted' | 'failed';
    } }) {
      const response = state.htmlSnapshotResponses[action.payload.responseId];
      if (!response || response.isComplete || !response.streamingStopped || !response.supportsRequestCancellation
        || response.agentId !== state.activeChatbotVariant
        || state.activeResponseByLegacyTarget[response.legacyTarget] !== response.responseId) return;
      response.backendCancellationStatus = action.payload.status;
    },
    stopAIResponse(state, action: { payload: string }) {
      const response = state.htmlSnapshotResponses[action.payload];
      if (!response || response.isComplete || response.streamingStopped
        || response.agentId !== state.activeChatbotVariant
        || state.activeResponseByLegacyTarget[response.legacyTarget] !== action.payload) return;
      response.streamingStopped = true;
      freezeProgress(response);
    },
    cancelAIResponse(state, action: { payload: string }) {
      const responseId = action.payload;

      for (const [target, activeResponseId] of Object.entries(
        state.activeResponseByLegacyTarget
      )) {
        if (activeResponseId === responseId) {
          delete state.activeResponseByLegacyTarget[target];
        }
      }

      state.conversations = state.conversations.filter(
        (item) => item.responseId !== responseId
      );
      delete state.htmlSnapshotResponses[responseId];

      if (state.pendingResponseId === responseId) {
        state.pendingResponseId = undefined;
      }
      state.aiResponseStatus = AIResponseStatusType.READY;
    },
    setAIResponseStatus(state, action) {
      if (action.payload === AIResponseStatusType.READY && Object.values(state.activeResponseByLegacyTarget).some(
        responseId => state.htmlSnapshotResponses[responseId]?.streamingStopped
          && !state.htmlSnapshotResponses[responseId]?.isComplete
      )) return;
      state.aiResponseStatus = action.payload;
      if (action.payload === AIResponseStatusType.ERROR) {
        for (const response of Object.values(state.htmlSnapshotResponses)) {
          freezeProgress(response);
        }
      }
      if (action.payload === AIResponseStatusType.PENDING) {
        state.pendingResponseMetadata = undefined;
      } else if (action.payload === AIResponseStatusType.READY) {
        const activeResponseIds = new Set(
          Object.values(state.activeResponseByLegacyTarget)
        );
        for (const responseId of activeResponseIds) {
          const response = state.htmlSnapshotResponses[responseId];
          if (response) {
            freezeProgress(response);
            response.isComplete = true;
          }
        }
        state.activeResponseByLegacyTarget = {};
      }
    },
    setAIResponseMetadata(state, action) {
      const metadata = normalizeAIResponseMetadata(action.payload);
      if (!metadata) return;

      state.pendingResponseMetadata = metadata;
      const responseId = state.pendingResponseId;
      const responseIndex = state.conversations.findIndex(
        (item) => item.responseId === responseId && !item.isSent
      );
      if (responseIndex >= 0) {
        state.conversations[responseIndex] = {
          ...state.conversations[responseIndex],
          ...metadata,
        };
      }
      state.pendingResponseId = undefined;
    },
    setIncrementalResponse(state, action) {
      const lastItem = state.conversations[state.conversations.length - 1];
      if (lastItem?.responseId && state.htmlSnapshotResponses[lastItem.responseId]?.streamingStopped) return;
      const newContent = action?.payload || '';
      const metadata = state.pendingResponseMetadata;

      if (!lastItem || lastItem.isSent) {
        const assistantMessage: ConversationItemType = {
          isSent: false,
          text: newContent,
          createdAt: new Date().toISOString(),
        };

        if (metadata?.aiModelName) assistantMessage.aiModelName = metadata.aiModelName;
        if (metadata?.aiReasoning) assistantMessage.aiReasoning = metadata.aiReasoning;

        state.conversations.push(assistantMessage);
      } else {
        lastItem.text = (lastItem.text || '') + newContent;
        lastItem.createdAt = lastItem.createdAt ?? new Date().toISOString();
        if (metadata?.aiModelName) lastItem.aiModelName = metadata.aiModelName;
        if (metadata?.aiReasoning) lastItem.aiReasoning = metadata.aiReasoning;
      }
    },
    setConversationSessionId(state, action) {
      for (const response of Object.values(state.htmlSnapshotResponses)) {
        if ((response.sessionId && response.sessionId !== action.payload)
          || (state.conversationSessionId && state.conversationSessionId !== action.payload)) {
          response.progressSuppressed = true;
          response.progressHistory = undefined;
        }
      }
      state.conversationSessionId = action.payload;
    },
    closeChatbot(state) {
      dismissCurrentOnboardingActions(state);
      state.isExternalOpen = false;
      // If a response was streaming, ensure we don't leave the UI stuck in
      // PRINTING/PENDING when the user closes the widget.
      for (const responseId of new Set(
        Object.values(state.activeResponseByLegacyTarget)
      )) {
        const response = state.htmlSnapshotResponses[responseId];
        if (response) {
          response.isComplete = true;
          response.progressHistory = undefined;
        }
      }
      state.aiResponseStatus = AIResponseStatusType.READY;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
    clearAndCloseChatbot(state) {
      dismissCurrentOnboardingActions(state);
      state.isExternalOpen = false;
      state.conversations = [];
      state.conversationSessionId = undefined;
      state.aiResponseStatus = AIResponseStatusType.READY;
      state.htmlSnapshotResponses = {};
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
      state.suggestedQuestions = [];
      state.isSummaryMode = false;
      state.pendingResponseMetadata = undefined;
    },
    addNewConversation(state, action?: { payload: string }) {
      state.isExternalOpen = true;

      // Clear any previous state to ensure fresh conversation
      state.suggestedQuestions = [];
      state.conversationSessionId = undefined;
      state.isSummaryMode = false;
      state.htmlSnapshotResponses = {};
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;

      if (action.payload) {
        // Start with user's prompt - this prevents welcome message from being sent
        state.conversations = [
          {
            isSent: true,
            text: action.payload,
            createdAt: new Date().toISOString(),
          },
        ];
        state.aiResponseStatus = AIResponseStatusType.PENDING;
      } else {
        // Start with empty conversation - this allows welcome message to be sent
        state.conversations = [];
        state.aiResponseStatus = AIResponseStatusType.READY;
      }
    },
    addNewConversationWithWelcome(state) {
      state.isExternalOpen = true;
      state.conversations = [];
      state.aiResponseStatus = AIResponseStatusType.PENDING;
      state.suggestedQuestions = [];
      state.conversationSessionId = undefined;
      state.isSummaryMode = false;
      state.htmlSnapshotResponses = {};
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
    requestRoomMayaOpen(state, action: { payload: string }) {
      state.pendingRoomMayaOpenId = action.payload;
    },
    clearRoomMayaOpenRequest(state, action: { payload: string }) {
      if (state.pendingRoomMayaOpenId === action.payload) {
        state.pendingRoomMayaOpenId = null;
      }
    },
    setContextMode(state, action: { payload: AIContextMode }) {
      state.contextMode = action.payload;
    },
    setActiveChatbotVariant(state, action: { payload: ChatbotVariant }) {
      if (state.activeChatbotVariant !== action.payload) {
        for (const response of Object.values(state.htmlSnapshotResponses)) {
          response.progressSuppressed = true;
          response.progressHistory = undefined;
        }
      }
      state.activeChatbotVariant = action.payload;
    },
    setActiveSubjectLabel(state, action: { payload: string | null }) {
      state.activeSubjectLabel = action.payload;
    },
    setOnboardingSection(state, action: { payload: OnboardingSectionInfo | null }) {
      state.onboardingSection = action.payload;
    },
    setOnboardingOverview(state, action: { payload: OnboardingOverview | null }) {
      state.onboardingOverview = action.payload;
    },
    resetChat(state) {
      state.conversations = [];
      state.conversationSessionId = undefined;
      state.aiResponseStatus = AIResponseStatusType.PENDING;
      state.htmlSnapshotResponses = {};
      state.suggestedQuestions = [];
      state.isSummaryMode = false;
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
    addDocumentSummaryRequest(state, action: { payload: string }) {
      // This action is specifically for document summaries and should NOT trigger welcome messages
      state.isExternalOpen = true;
      state.conversations = [];
      state.aiResponseStatus = AIResponseStatusType.PENDING;
      state.suggestedQuestions = [];
      state.conversationSessionId = undefined;
      state.isSummaryMode = true;
      state.htmlSnapshotResponses = {};
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
    loadHistoricalSession(
      state,
      action: {
        payload: {
          sessionId: string;
          conversations: ConversationItemType[];
        };
      }
    ) {
      state.isExternalOpen = true;
      state.conversationSessionId = action.payload.sessionId;
      state.conversations = action.payload.conversations;
      state.aiResponseStatus = AIResponseStatusType.READY;
      state.htmlSnapshotResponses = {};
      state.suggestedQuestions = [];
      state.isSummaryMode = false;
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
    restoreVariantState(
      state,
      action: {
        payload: {
          conversations: ConversationItemType[];
          conversationSessionId: string | undefined;
          suggestedQuestions: string[];
          isSummaryMode: boolean;
          htmlSnapshotResponses: Record<string, HtmlSnapshotResponseState>;
        };
      }
    ) {
      state.isExternalOpen = true;
      state.conversations = action.payload.conversations;
      state.conversationSessionId = action.payload.conversationSessionId;
      state.suggestedQuestions = action.payload.suggestedQuestions;
      state.isSummaryMode = action.payload.isSummaryMode;
      state.htmlSnapshotResponses = action.payload.htmlSnapshotResponses;
      state.aiResponseStatus = AIResponseStatusType.READY;
      state.pendingResponseMetadata = undefined;
      state.activeResponseByLegacyTarget = {};
      state.pendingResponseId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIPrompts.pending, (state) => {
        state.loadingPrompts = true;
      })
      .addCase(fetchAIPrompts.fulfilled, (state, action) => {
        state.suggestedQuestions = action.payload;
        state.loadingPrompts = false;
      })
      .addCase(fetchAIPrompts.rejected, (state) => {
        state.loadingPrompts = false;
      });
  },
});

export const {
  requestRoomQuickReply,
  clearRoomQuickReply,
  completeOnboardingActions,
  dismissOnboardingActions,
  dismissCompletedOnboardingSteps,
  applyChatProgress,
  applyChatActionOptions,
  suppressAIProgress,
  addConversationItem,
  beginAIResponse,
  applyHtmlSnapshot,
  appendLegacyResponseContent,
  completeAIResponse,
  stopAIResponse,
  setAIResponseCancellationSupport,
  setAIResponseBackendCancellationStatus,
  cancelAIResponse,
  setAIResponseStatus,
  setAIResponseMetadata,
  setIncrementalResponse,
  setConversationSessionId,
  closeChatbot,
  clearAndCloseChatbot,
  addNewConversation,
  addNewConversationWithWelcome,
  requestRoomMayaOpen,
  clearRoomMayaOpenRequest,
  addDocumentSummaryRequest,
  setContextMode,
  setActiveChatbotVariant,
  setActiveSubjectLabel,
  setOnboardingSection,
  setOnboardingOverview,
  resetChat,
  loadHistoricalSession,
  restoreVariantState,
} = aiChatbotSlice.actions;

export default aiChatbotSlice.reducer;
